import React from 'react';
import PropTypes from 'prop-types';
import {OrderedMap} from 'immutable';
import _ from 'lodash';

export const ModalManagerContext = React.createContext();

ModalManagerProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
export function ModalManagerProvider({children}) {
    let [modals, setModals] = React.useState(new OrderedMap());

    // Add reference to `modals` so that `addModal`, and `onPopState` can be immutable
    const modalsRef = React.useRef();
    modalsRef.current = modals;

    // This will help us keep track of when the last modal is being closed
    const isLastModal = React.useRef(false);

    // An immutable function that adds a modal to the list along with its cleanup function
    // which is also returned to the caller
    const addModal = React.useCallback(onCloseRefOrFn => {
        const modalId = _.uniqueId('modal');

        // Add the modal along with its cleanup function
        setModals(_modals => {
            if (_modals.isEmpty()) {
                // Now: INITIAL STATE
                // ...
                // When opening the 1st modal, we push onto the history
                // so that if the user presses back while there is an open
                // modal, we stay on the current route. We will clean this up later
                // when the last modal is closed.
                window.history.pushState(window.history.state, null);
                // Now: INITIAL STATE + 1
            }
            return _modals.set(modalId, onCloseRefOrFn);
        });

        // A function to remove the modal from state, when
        const removeModal = () =>
            setModals(_modals => {
                const updated = _modals.remove(modalId);
                // We are removing the last modal, so we should make sure to remove the temporary state
                // that we pushed earlier. So that the user doesn't have to press "BACK" twice after closing
                // the last modal.
                isLastModal.current = updated.isEmpty() && _modals.has(modalId);
                return updated;
            });

        // Return the modal cleanup function
        return removeModal;
    }, []);

    // Add a popstate handler
    React.useEffect(() => {
        // When the user goes back, stop tracking the modal & manage the history
        const onPopState = event => {
            // The user went "back" in their history
            // Now: INITIAL STATE
                if (!modalsRef.current.isEmpty()) {
                // If we are tracking open modals, then we should close the most recently opened
                // modal, and push onto the history

                // Get the most recently opened modal
                const [currentModalId, onCloseRefOrFn] = modalsRef.current.entrySeq().last();
                // console.log(`%c intercepting for ${currentModalId}`, "background-color: yellow; color: black; font-size: 2em;");

                // Remove it from the list
                const updated = modalsRef.current.remove(currentModalId);

                // Go back to INITIAL STATE + 1
                // We do this even if there are no more tracked modals, so that we can have consistent
                // cleanup using `isLastModal` regardless of how the last modal is closed
                window.history.pushState(event.state, null);

                // Call the modals `onClose` handler - hopefully this will close the modal
                const onClose = (onCloseRefOrFn && onCloseRefOrFn.current) || onCloseRefOrFn;
                if (onClose) {
                    onClose();
                }

                // Stop tracking the modal
                setModals(updated);

                // We keep track of when we just closed the last modal
                isLastModal.current = updated.isEmpty();
            } else if (isLastModal.current) {
                // The last modal was already closed
                isLastModal.current = false;
                window.history.back();
            }
        };

        window.addEventListener('popstate', onPopState, true);
    }, []);

    return <ModalManagerContext.Provider value={addModal}>{children}</ModalManagerContext.Provider>;
}

export default function useModalManager() {
    return React.useContext(ModalManagerContext);
}
